import { useEffect, useMemo, useState, type ReactNode } from "react";

import { anfrage, ApiFehler } from "../../api/client";
import { Spinner } from "../../komponenten/Basis";
import { MarkenKopf, RechtsLinks } from "../../komponenten/MarkenKopf";
import { Unterschrift } from "../../komponenten/Unterschrift";
import { quadratischesIcon, seitenMarkeSetzen } from "../../lib/markenIcon";

/**
 * Öffentliche Seite zum Kunden-Link (status.<domain>/formular#<token>): Der Endkunde macht seine Angaben,
 * unterschreibt die Vollmacht bzw. beantwortet Rückfragen. Mobil zuerst, ohne Login.
 * Der Token steht hinter „#“ (nie in einem Server-Log) und geht nur im Body an die API.
 */

type FrageTyp = "ja_nein" | "text" | "datei";
interface Frage {
  id: string;
  text: string;
  typ: FrageTyp;
}
interface FormularInfo {
  art: "erfassung" | "rueckfrage";
  status: "offen" | "abgeschlossen";
  kunde_name: string;
  ort: string | null;
  betrieb_name: string | null;
  vollmacht: { titel: string; bevollmaechtigter: string; absaetze: string[]; datenschutz: string; version: string } | null;
  fragen: Frage[];
  pdf_verfuegbar: boolean;
  betrieb_logo: string | null;
  nur_betrieb: boolean;
  impressum_url: string | null;
  datenschutz_url: string | null;
}

interface Person {
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  telefon: string;
  email: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
}
interface AnlageAngaben {
  standort_wie_wohnort: boolean;
  standort_strasse: string;
  standort_hausnummer: string;
  standort_plz: string;
  standort_ort: string;
  zaehlernummer: string;
  iban: string;
  kontoinhaber: string;
}

const LEER_PERSON: Person = { vorname: "", nachname: "", geburtsdatum: "", telefon: "", email: "", strasse: "", hausnummer: "", plz: "", ort: "" };
const LEER_ANLAGE: AnlageAngaben = {
  standort_wie_wohnort: true, standort_strasse: "", standort_hausnummer: "", standort_plz: "", standort_ort: "",
  zaehlernummer: "", iban: "", kontoinhaber: "",
};

const API = "/api/status/formular";

function token(): string {
  return window.location.hash.replace(/^#/, "").trim();
}

// Entwurf im Tab merken (z. B. wenn der Kunde kurz in die Banking-App wechselt). Ohne Unterschrift.
function entwurfLaden<T>(schluessel: string, standard: T): T {
  try {
    const roh = sessionStorage.getItem(`am-formular-${schluessel}`);
    return roh ? { ...standard, ...(JSON.parse(roh) as T) } : standard;
  } catch {
    return standard;
  }
}
function entwurfSpeichern(schluessel: string, wert: unknown): void {
  try {
    sessionStorage.setItem(`am-formular-${schluessel}`, JSON.stringify(wert));
  } catch {
    // ohne Speicher geht es trotzdem
  }
}
function entwurfLoeschen(): void {
  try {
    Object.keys(sessionStorage).filter((k) => k.startsWith("am-formular-")).forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // egal
  }
}

async function dateiHochladen(frageId: string, datei: File): Promise<number> {
  const formular = new FormData();
  formular.append("token", token());
  formular.append("frage_id", frageId);
  formular.append("file", datei);
  let antwort: Response;
  try {
    antwort = await fetch(`${API}/datei`, { method: "POST", body: formular });
  } catch {
    throw new Error("Keine Verbindung – bitte erneut versuchen.");
  }
  const huelle = (await antwort.json().catch(() => null)) as { data?: { dokument_id: number }; error?: { message?: string } } | null;
  if (!antwort.ok || !huelle?.data) throw new Error(huelle?.error?.message ?? "Hochladen fehlgeschlagen");
  return huelle.data.dokument_id;
}

async function pdfLaden(): Promise<void> {
  const antwort = await fetch(`${API}/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: token() }),
  });
  if (!antwort.ok) throw new Error("Das PDF steht nicht mehr bereit.");
  const blob = await antwort.blob();
  // Dateiname vom Server (z. B. „Vollmacht und Kundenangaben (01.10.2026).pdf“), ohne AnmeldeMeister im Namen
  const treffer = /filename\*=UTF-8''([^;]+)/.exec(antwort.headers.get("Content-Disposition") ?? "");
  const dateiname = treffer?.[1] ? decodeURIComponent(treffer[1]) : "Ihre Angaben.pdf";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---------------------------------------------------------------- kleine Bausteine

function Rahmen({ children, info }: { children: ReactNode; info?: FormularInfo | null }) {
  return (
    <div className="min-h-screen bg-neutral-100 px-4 py-6">
      <div className="mx-auto w-full max-w-lg space-y-4">
        <MarkenKopf bereit={Boolean(info)} nurBetrieb={info?.nur_betrieb ?? false} logo={info?.betrieb_logo} name={info?.betrieb_name} />
        <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-karte sm:p-7">{children}</div>
        <p className="text-center text-xs text-neutral-400">
          Ihre Angaben werden verschlüsselt übertragen.
        </p>
        <RechtsLinks bereit={Boolean(info)} nurBetrieb={info?.nur_betrieb ?? false} impressum={info?.impressum_url} datenschutz={info?.datenschutz_url} />
      </div>
    </div>
  );
}

function Feld({
  id, label, wert, setzen, typ = "text", fehler, hinweis, autoComplete, inputMode, pflicht = true,
}: {
  id: string; label: string; wert: string; setzen: (w: string) => void; typ?: string; fehler?: string; hinweis?: string;
  autoComplete?: string; inputMode?: "text" | "numeric" | "tel" | "email"; pflicht?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold">
        {label} {!pflicht && <span className="font-normal text-neutral-400">(optional)</span>}
      </label>
      <input
        id={id}
        type={typ}
        value={wert}
        autoComplete={autoComplete}
        inputMode={inputMode}
        onChange={(e) => setzen(e.target.value)}
        className={`w-full rounded-xl border px-3 py-3 text-base ${fehler ? "border-ampel-rot" : "border-neutral-300"}`}
      />
      {hinweis && !fehler && <p className="mt-1 text-xs text-neutral-500">{hinweis}</p>}
      {fehler && <p className="mt-1 text-xs font-semibold text-ampel-rot">{fehler}</p>}
    </div>
  );
}

function Weiter({ children, onClick, disabled, laedt }: { children: ReactNode; onClick: () => void; disabled?: boolean; laedt?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || laedt}
      className="flex w-full items-center justify-center gap-2 rounded-xl bg-marke px-4 py-3.5 text-base font-semibold text-white disabled:bg-neutral-300"
    >
      {laedt && <Spinner klein />}
      {children}
    </button>
  );
}

function Zurueck({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="mt-3 w-full py-2 text-sm font-semibold text-neutral-600">
      ← Zurück
    </button>
  );
}

function DateiKnopf({ frageId, text, dateien, setDateien }: { frageId: string; text: string; dateien: string[]; setDateien: (d: string[]) => void }) {
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function gewaehlt(e: React.ChangeEvent<HTMLInputElement>) {
    const datei = e.target.files?.[0];
    e.target.value = "";
    if (!datei) return;
    setFehler(null);
    setLaedt(true);
    try {
      await dateiHochladen(frageId, datei);
      setDateien([...dateien, datei.name]);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Hochladen fehlgeschlagen");
    } finally {
      setLaedt(false);
    }
  }
  return (
    <div>
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-300 px-4 py-4 text-sm font-semibold text-neutral-700">
        {laedt ? <Spinner klein /> : "📷"} {text}
        <input type="file" accept="image/*,application/pdf,.docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword" capture="environment" className="sr-only" onChange={gewaehlt} disabled={laedt} />
      </label>
      {dateien.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm text-ampel-gruen">
          {dateien.map((n, i) => <li key={`${n}-${i}`}>✓ {n}</li>)}
        </ul>
      )}
      {fehler && <p className="mt-1 text-xs font-semibold text-ampel-rot">{fehler}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- Seite

type Schritt = "start" | "person" | "anlage" | "fragen" | "vollmacht" | "pruefen" | "fertig";

export function FormularSeite() {
  const [info, setInfo] = useState<FormularInfo | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [schritt, setSchritt] = useState<Schritt>("start");
  const [person, setPerson] = useState<Person>(() => entwurfLaden("person", LEER_PERSON));
  const [anlage, setAnlage] = useState<AnlageAngaben>(() => entwurfLaden("anlage", LEER_ANLAGE));
  const [antworten, setAntworten] = useState<Record<string, string | boolean>>({});
  const [dateien, setDateien] = useState<Record<string, string[]>>({});
  const [bestaetigt, setBestaetigt] = useState(false);
  const [ortUnterschrift, setOrtUnterschrift] = useState("");
  const [unterschrift, setUnterschrift] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    document.title = "Ihre Angaben";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!token()) {
      setLadeFehler("Dieser Link ist unvollständig. Bitte öffnen Sie ihn noch einmal aus der Nachricht.");
      return;
    }
    anfrage<FormularInfo>(`${API}/laden`, { methode: "POST", body: { token: token() } })
      .then((d) => {
        setInfo(d);
        if (d.status === "abgeschlossen") setSchritt("fertig");
      })
      .catch((e: unknown) =>
        setLadeFehler(
          e instanceof ApiFehler && e.status === 404
            ? "Dieser Link ist nicht mehr gültig. Bitte fordern Sie bei Ihrem Installateur einen neuen an."
            : "Die Seite konnte gerade nicht geladen werden. Bitte versuchen Sie es gleich noch einmal.",
        ),
      );
  }, []);

  useEffect(() => {
    if (!info?.nur_betrieb || !info.betrieb_name) return;
    let aufraeumen = () => {};
    let aktiv = true;
    const name = info.betrieb_name;
    void (info.betrieb_logo ? quadratischesIcon(info.betrieb_logo) : Promise.resolve(null)).then((icon) => {
      if (aktiv) aufraeumen = seitenMarkeSetzen(`Ihre Angaben – ${name}`, icon);
    });
    return () => {
      aktiv = false;
      aufraeumen();
    };
  }, [info?.nur_betrieb, info?.betrieb_name, info?.betrieb_logo]);

  // Anderer Link im selben Tab (nur der Teil hinter „#“ ändert sich): sauber neu laden.
  useEffect(() => {
    const neuLaden = () => window.location.reload();
    window.addEventListener("hashchange", neuLaden);
    return () => window.removeEventListener("hashchange", neuLaden);
  }, []);

  useEffect(() => entwurfSpeichern("person", person), [person]);
  useEffect(() => entwurfSpeichern("anlage", anlage), [anlage]);
  useEffect(() => {
    if (!ortUnterschrift && person.ort) setOrtUnterschrift(person.ort);
  }, [person.ort, ortUnterschrift]);

  const schritte = useMemo<Schritt[]>(() => {
    if (!info) return [];
    const liste: Schritt[] = [];
    if (info.art === "erfassung") liste.push("person", "anlage");
    if (info.fragen.length) liste.push("fragen");
    if (info.vollmacht) liste.push("vollmacht");
    liste.push("pruefen");
    return liste;
  }, [info]);

  function naechster() {
    setMeldung(null);
    const i = schritte.indexOf(schritt);
    setSchritt(schritte[i + 1] ?? "pruefen");
    window.scrollTo(0, 0);
  }
  function voriger() {
    const i = schritte.indexOf(schritt);
    setSchritt(i <= 0 ? "start" : (schritte[i - 1] ?? "start"));
    window.scrollTo(0, 0);
  }

  const p = (feld: keyof Person) => (w: string) => setPerson({ ...person, [feld]: w });
  const a = (feld: keyof AnlageAngaben) => (w: string) => setAnlage({ ...anlage, [feld]: w });

  function personPruefen(): boolean {
    const f: Record<string, string> = {};
    (Object.keys(LEER_PERSON) as (keyof Person)[]).forEach((k) => {
      if (!person[k].trim()) f[k] = "Bitte ausfüllen";
    });
    if (person.plz && !/^\d{5}$/.test(person.plz.trim())) f.plz = "5 Ziffern";
    if (person.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(person.email.trim())) f.email = "Bitte gültige E-Mail angeben";
    setFehler(f);
    return Object.keys(f).length === 0;
  }
  function anlagePruefen(): boolean {
    const f: Record<string, string> = {};
    if (!anlage.standort_wie_wohnort) {
      if (!anlage.standort_strasse.trim()) f.standort_strasse = "Bitte ausfüllen";
      if (!/^\d{5}$/.test(anlage.standort_plz.trim())) f.standort_plz = "5 Ziffern";
      if (!anlage.standort_ort.trim()) f.standort_ort = "Bitte ausfüllen";
    }
    setFehler(f);
    return Object.keys(f).length === 0;
  }
  function fragenBeantwortet(): boolean {
    return (info?.fragen ?? []).every((fr) =>
      fr.typ === "datei" ? (dateien[fr.id] ?? []).length > 0 : fr.typ === "ja_nein" ? typeof antworten[fr.id] === "boolean" : String(antworten[fr.id] ?? "").trim() !== "",
    );
  }

  async function absenden() {
    if (!info) return;
    setMeldung(null);
    setSendet(true);
    const leer = (w: string) => (w.trim() ? w.trim() : null);
    try {
      const daten = await anfrage<FormularInfo>(`${API}/absenden`, {
        methode: "POST",
        body: {
          token: token(),
          person: info.art === "erfassung" ? { ...person, plz: person.plz.trim() } : null,
          anlage:
            info.art === "erfassung"
              ? {
                  standort_wie_wohnort: anlage.standort_wie_wohnort,
                  standort_strasse: anlage.standort_wie_wohnort ? null : leer(anlage.standort_strasse),
                  standort_hausnummer: anlage.standort_wie_wohnort ? null : leer(anlage.standort_hausnummer),
                  standort_plz: anlage.standort_wie_wohnort ? null : leer(anlage.standort_plz),
                  standort_ort: anlage.standort_wie_wohnort ? null : leer(anlage.standort_ort),
                  zaehlernummer: leer(anlage.zaehlernummer),
                  iban: leer(anlage.iban),
                  kontoinhaber: leer(anlage.kontoinhaber),
                }
              : null,
          unterschrift_png: info.vollmacht ? unterschrift : null,
          unterschrift_ort: info.vollmacht ? leer(ortUnterschrift) : null,
          vollmacht_bestaetigt: bestaetigt,
          antworten: Object.fromEntries(Object.entries(antworten).filter(([, w]) => w !== "")),
        },
      });
      setInfo(daten);
      entwurfLoeschen();
      setSchritt("fertig");
      window.scrollTo(0, 0);
    } catch (e) {
      if (e instanceof ApiFehler) {
        // Fachliche Prüfung liefert {feld: meldung}, die Formatprüfung [{feld: "person.email", meldung}].
        const felder: Record<string, string> = {};
        if (Array.isArray(e.details)) {
          for (const d of e.details as { feld?: string; meldung?: string }[]) {
            const name = (d.feld ?? "").split(".").pop();
            if (name) felder[name] = d.meldung ?? "Bitte prüfen";
          }
        } else if (e.code === "angaben_ungueltig" && e.details && typeof e.details === "object") {
          Object.assign(felder, e.details as Record<string, string>);
        }
        if (Object.keys(felder).length > 0) {
          setFehler(felder);
          const inPerson = Object.keys(felder).some((k) => k in LEER_PERSON);
          setMeldung("Bitte prüfen Sie die markierten Angaben.");
          setSchritt(inPerson ? "person" : "anlage");
          window.scrollTo(0, 0);
        } else {
          setMeldung(e.message);
        }
      } else {
        setMeldung("Absenden fehlgeschlagen – bitte erneut versuchen.");
      }
    } finally {
      setSendet(false);
    }
  }

  if (ladeFehler) {
    return (
      <Rahmen info={null}>
        <h1 className="text-xl font-semibold">Link nicht verfügbar</h1>
        <p className="mt-3 text-sm text-neutral-600">{ladeFehler}</p>
      </Rahmen>
    );
  }
  if (!info) {
    return (
      <Rahmen>
        <div className="flex justify-center py-8"><Spinner label="Wird geladen…" /></div>
      </Rahmen>
    );
  }

  const auftraggeber = info.betrieb_name ?? "AnmeldeMeister";
  const nummer = schritte.indexOf(schritt) + 1;
  const kopf =
    schritt !== "start" && schritt !== "fertig" ? (
      <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">Schritt {nummer} von {schritte.length}</p>
    ) : null;

  return (
    <Rahmen info={info}>
      {kopf}
      {meldung && schritt !== "fertig" && (
        <div className="mb-4 rounded-xl border border-ampel-rot/30 bg-ampel-rotHell px-4 py-3 text-sm text-ampel-rot">{meldung}</div>
      )}

      {schritt === "start" && (
        <div className="space-y-4">
          <h1 className="text-2xl font-semibold">Guten Tag, {info.kunde_name}</h1>
          {info.art === "erfassung" ? (
            <p className="text-neutral-700">
              Damit wir Ihre Anlage{info.ort ? ` in ${info.ort}` : ""} beim Netzbetreiber und im Marktstammdatenregister anmelden können,
              brauchen wir ein paar Angaben von Ihnen{info.vollmacht ? " und Ihre Unterschrift unter die Vollmacht" : ""}. Das dauert etwa 3 Minuten.
            </p>
          ) : (
            <p className="text-neutral-700">
              Zur Anmeldung Ihrer Anlage{info.ort ? ` in ${info.ort}` : ""} haben wir noch {info.fragen.length === 1 ? "eine kurze Frage" : `${info.fragen.length} kurze Fragen`}.
            </p>
          )}
          {!info.nur_betrieb && <p className="text-sm text-neutral-500">Im Auftrag von {auftraggeber}.</p>}
          {info.art === "erfassung" && (
            <p className="rounded-xl bg-neutral-50 p-3 text-sm text-neutral-600">
              Halten Sie am besten bereit: Ihre Bankverbindung (für die Einspeisevergütung) und, falls zur Hand, die Nummer Ihres Stromzählers.
            </p>
          )}
          <Weiter onClick={() => { setSchritt(schritte[0] ?? "pruefen"); window.scrollTo(0, 0); }}>Los geht’s</Weiter>
        </div>
      )}

      {schritt === "person" && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Ihre persönlichen Angaben</h2>
          <p className="text-sm text-neutral-500">Als Anlagenbetreiber – so, wie es im Marktstammdatenregister stehen soll.</p>
          <div className="grid grid-cols-2 gap-3">
            <Feld id="vorname" label="Vorname" wert={person.vorname} setzen={p("vorname")} fehler={fehler.vorname} autoComplete="given-name" />
            <Feld id="nachname" label="Nachname" wert={person.nachname} setzen={p("nachname")} fehler={fehler.nachname} autoComplete="family-name" />
          </div>
          <Feld id="geburtsdatum" label="Geburtsdatum" typ="date" wert={person.geburtsdatum} setzen={p("geburtsdatum")} fehler={fehler.geburtsdatum} autoComplete="bday" />
          <Feld id="telefon" label="Telefon" typ="tel" inputMode="tel" wert={person.telefon} setzen={p("telefon")} fehler={fehler.telefon} autoComplete="tel" />
          <Feld id="email" label="E-Mail" typ="email" inputMode="email" wert={person.email} setzen={p("email")} fehler={fehler.email} autoComplete="email" />
          <p className="pt-2 text-sm font-semibold">Ihre Wohnanschrift</p>
          <div className="grid grid-cols-[1fr_6rem] gap-3">
            <Feld id="strasse" label="Straße" wert={person.strasse} setzen={p("strasse")} fehler={fehler.strasse} autoComplete="address-line1" />
            <Feld id="hausnummer" label="Nr." wert={person.hausnummer} setzen={p("hausnummer")} fehler={fehler.hausnummer} />
          </div>
          <div className="grid grid-cols-[6rem_1fr] gap-3">
            <Feld id="plz" label="PLZ" inputMode="numeric" wert={person.plz} setzen={p("plz")} fehler={fehler.plz} autoComplete="postal-code" />
            <Feld id="ort" label="Ort" wert={person.ort} setzen={p("ort")} fehler={fehler.ort} autoComplete="address-level2" />
          </div>
          <Weiter onClick={() => personPruefen() && naechster()}>Weiter</Weiter>
          <Zurueck onClick={voriger} />
        </div>
      )}

      {schritt === "anlage" && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Anlage und Zähler</h2>
          <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 accent-neutral-900"
              checked={anlage.standort_wie_wohnort}
              onChange={(e) => setAnlage({ ...anlage, standort_wie_wohnort: e.target.checked })}
            />
            <span className="text-sm">Die Anlage steht an meiner Wohnanschrift</span>
          </label>
          {!anlage.standort_wie_wohnort && (
            <>
              <div className="grid grid-cols-[1fr_6rem] gap-3">
                <Feld id="st_strasse" label="Straße der Anlage" wert={anlage.standort_strasse} setzen={a("standort_strasse")} fehler={fehler.standort_strasse ?? fehler.standort} />
                <Feld id="st_nr" label="Nr." wert={anlage.standort_hausnummer} setzen={a("standort_hausnummer")} pflicht={false} />
              </div>
              <div className="grid grid-cols-[6rem_1fr] gap-3">
                <Feld id="st_plz" label="PLZ" inputMode="numeric" wert={anlage.standort_plz} setzen={a("standort_plz")} fehler={fehler.standort_plz} />
                <Feld id="st_ort" label="Ort" wert={anlage.standort_ort} setzen={a("standort_ort")} fehler={fehler.standort_ort} />
              </div>
            </>
          )}
          <Feld
            id="zaehlernummer" label="Zählernummer" pflicht={false} wert={anlage.zaehlernummer} setzen={a("zaehlernummer")} fehler={fehler.zaehlernummer}
            hinweis="Steht auf Ihrem Stromzähler, z. B. „1ESY1160…“. Wenn Sie sie nicht finden, einfach ein Foto machen."
          />
          <DateiKnopf frageId="zaehlerfoto" text="Foto vom Zähler (optional)" dateien={dateien.zaehlerfoto ?? []} setDateien={(d) => setDateien({ ...dateien, zaehlerfoto: d })} />
          <p className="pt-2 text-sm font-semibold">Bankverbindung für die Einspeisevergütung</p>
          <Feld id="iban" label="IBAN" pflicht={false} wert={anlage.iban} setzen={a("iban")} fehler={fehler.iban} autoComplete="off" hinweis="Der Netzbetreiber zahlt die Vergütung auf dieses Konto." />
          <Feld id="kontoinhaber" label="Kontoinhaber, falls abweichend" pflicht={false} wert={anlage.kontoinhaber} setzen={a("kontoinhaber")} />
          <Weiter onClick={() => anlagePruefen() && naechster()}>Weiter</Weiter>
          <Zurueck onClick={voriger} />
        </div>
      )}

      {schritt === "fragen" && (
        <div className="space-y-5">
          <h2 className="text-xl font-semibold">{info.fragen.length === 1 ? "Unsere Frage" : "Unsere Fragen"}</h2>
          {info.fragen.map((fr) => (
            <div key={fr.id} className="space-y-2">
              <p className="font-semibold">{fr.text}</p>
              {fr.typ === "ja_nein" && (
                <div className="grid grid-cols-2 gap-3">
                  {([true, false] as const).map((w) => (
                    <button
                      key={String(w)}
                      type="button"
                      onClick={() => setAntworten({ ...antworten, [fr.id]: w })}
                      className={`rounded-xl border-2 py-3 text-base font-semibold ${antworten[fr.id] === w ? "border-marke bg-marke text-white" : "border-neutral-300"}`}
                    >
                      {w ? "Ja" : "Nein"}
                    </button>
                  ))}
                </div>
              )}
              {fr.typ === "text" && (
                <textarea
                  rows={3}
                  maxLength={2000}
                  className="w-full rounded-xl border border-neutral-300 px-3 py-3 text-base"
                  value={String(antworten[fr.id] ?? "")}
                  onChange={(e) => setAntworten({ ...antworten, [fr.id]: e.target.value })}
                />
              )}
              {fr.typ === "datei" && (
                <DateiKnopf frageId={fr.id} text="Foto oder Datei hinzufügen" dateien={dateien[fr.id] ?? []} setDateien={(d) => setDateien({ ...dateien, [fr.id]: d })} />
              )}
            </div>
          ))}
          <Weiter onClick={naechster} disabled={!fragenBeantwortet()}>Weiter</Weiter>
          <Zurueck onClick={voriger} />
        </div>
      )}

      {schritt === "vollmacht" && info.vollmacht && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">{info.vollmacht.titel}</h2>
          <div className="max-h-80 space-y-2 overflow-y-auto rounded-xl bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-800">
            {info.vollmacht.absaetze.map((absatz, i) => <p key={i}>{absatz}</p>)}
            <p className="text-xs text-neutral-500">{info.vollmacht.datenschutz}</p>
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3">
            <input type="checkbox" className="mt-1 h-5 w-5 accent-neutral-900" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} />
            <span className="text-sm">Ich habe die Vollmacht gelesen und erteile sie.</span>
          </label>
          <Feld id="unterschrift_ort" label="Ort" wert={ortUnterschrift} setzen={setOrtUnterschrift} />
          <Unterschrift beiAenderung={setUnterschrift} />
          <Weiter onClick={naechster} disabled={!bestaetigt || !unterschrift || !ortUnterschrift.trim()}>Weiter</Weiter>
          <Zurueck onClick={voriger} />
        </div>
      )}

      {schritt === "pruefen" && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Alles richtig?</h2>
          <dl className="divide-y divide-neutral-100 rounded-xl border border-neutral-200 text-sm">
            {info.art === "erfassung" && (
              <>
                <Zeile label="Name">{person.vorname} {person.nachname}</Zeile>
                <Zeile label="Geburtsdatum">{person.geburtsdatum.split("-").reverse().join(".")}</Zeile>
                <Zeile label="Anschrift">{person.strasse} {person.hausnummer}, {person.plz} {person.ort}</Zeile>
                <Zeile label="Kontakt">{person.telefon} · {person.email}</Zeile>
                <Zeile label="Anlage">
                  {anlage.standort_wie_wohnort ? "an der Wohnanschrift" : `${anlage.standort_strasse} ${anlage.standort_hausnummer}, ${anlage.standort_plz} ${anlage.standort_ort}`}
                </Zeile>
                <Zeile label="Zählernummer">{anlage.zaehlernummer || ((dateien.zaehlerfoto ?? []).length ? "per Foto" : "wird nachgereicht")}</Zeile>
                <Zeile label="IBAN">{anlage.iban || "wird nachgereicht"}</Zeile>
              </>
            )}
            {info.fragen.map((fr) => (
              <Zeile key={fr.id} label={fr.text}>
                {fr.typ === "datei" ? `${(dateien[fr.id] ?? []).length} Datei(en)` : typeof antworten[fr.id] === "boolean" ? (antworten[fr.id] ? "Ja" : "Nein") : String(antworten[fr.id] ?? "")}
              </Zeile>
            ))}
            {info.vollmacht && <Zeile label="Vollmacht">unterschrieben in {ortUnterschrift}</Zeile>}
          </dl>
          <Weiter onClick={absenden} laedt={sendet}>Absenden</Weiter>
          <Zurueck onClick={voriger} />
        </div>
      )}

      {schritt === "fertig" && (
        <div className="space-y-4 text-center">
          <p className="text-4xl" aria-hidden>✓</p>
          <h2 className="text-xl font-semibold">Vielen Dank!</h2>
          <p className="text-neutral-700">Ihre Angaben sind bei uns angekommen. Wir kümmern uns um die Anmeldung und melden uns, falls noch etwas fehlt.</p>
          {info.pdf_verfuegbar && (
            <>
              <Weiter onClick={() => void pdfLaden().catch((e: Error) => setMeldung(e.message))}>
                {info.vollmacht ? "Vollmacht als PDF speichern" : "Kopie als PDF speichern"}
              </Weiter>
              <p className="text-xs text-neutral-500">Der Download steht 24 Stunden bereit.</p>
            </>
          )}
          {meldung && <p className="text-sm text-ampel-rot">{meldung}</p>}
        </div>
      )}
    </Rahmen>
  );
}

function Zeile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3 px-3 py-2.5">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="min-w-0 font-medium [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}
